/* ============================================================
   slideshow by studiolab — isolated carousel controller
   Namespace: sl-slideshow-by-studiolab / sl-sbs
   ============================================================ */

(function () {
  'use strict';

  if (customElements.get('slideshow-by-studiolab')) return;

  class SlideshowByStudiolab extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.track = this.querySelector('[data-slideshow-by-studiolab-track]');
      this.frame = this.querySelector('.sl-slideshow-by-studiolab__frame');
      this.slides = Array.from(this.querySelectorAll('[data-slideshow-by-studiolab-slide]'));
      this.autoplayControl = this.querySelector('[data-slideshow-by-studiolab-autoplay-control]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.userPaused = false;

      if (!this.track || !this.slides.length) return;

      this.initialized = true;
      this.onAutoplayClick = this.onAutoplayClick.bind(this);
      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onWheel = this.onWheel.bind(this);
      this.scheduleHeightMeasurement = this.scheduleHeightMeasurement.bind(this);

      if (this.slides.length > 1) {
        this.initCarousel();
      } else {
        this.updateActiveSlide(0);
      }

      this.bindEvents();
      this.initHeightObserver();
      this.scheduleHeightMeasurement();
    }

    disconnectedCallback() {
      this.unbindEvents();
      if (this.heightObserver) {
        this.heightObserver.disconnect();
        this.heightObserver = null;
      }
      if (this.heightFrame) window.cancelAnimationFrame(this.heightFrame);
      if (this.flkty) {
        this.flkty.destroy();
        this.flkty = null;
      }
      this.initialized = false;
    }

    initCarousel() {
      const Flickity = window.theme && window.theme.Flickity;
      if (!Flickity) {
        this.updateActiveSlide(0);
        return;
      }

      const autoplayEnabled = this.dataset.autoplay === 'true' && !this.reducedMotion;
      const duration = Number.parseInt(this.dataset.duration, 10) || 5000;
      const useFade = this.dataset.transition !== 'slide';

      this.flkty = new Flickity(this.track, {
        accessibility: true,
        adaptiveHeight: false,
        autoPlay: autoplayEnabled ? duration : false,
        cellAlign: 'left',
        cellSelector: '[data-slideshow-by-studiolab-slide]',
        contain: true,
        draggable: this.slides.length > 1,
        fade: useFade,
        imagesLoaded: true,
        pageDots: this.dataset.navigationDots === 'true',
        pauseAutoPlayOnHover: true,
        prevNextButtons: this.dataset.navigationArrows === 'true',
        rightToLeft: document.documentElement.dir === 'rtl',
        setGallerySize: false,
        wrapAround: this.dataset.loop === 'true'
      });

      this.flkty.on('ready', () => this.updateActiveSlide(this.flkty.selectedIndex || 0));
      this.flkty.on('change', (index) => this.updateActiveSlide(index));
      this.updateActiveSlide(this.flkty.selectedIndex || 0);
    }

    bindEvents() {
      if (this.autoplayControl) {
        this.autoplayControl.addEventListener('click', this.onAutoplayClick);
      }

      this.addEventListener('mouseenter', this.onPointerEnter);
      this.addEventListener('mouseleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('wheel', this.onWheel, { passive: false });
      window.addEventListener('resize', this.scheduleHeightMeasurement);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    unbindEvents() {
      if (this.autoplayControl) {
        this.autoplayControl.removeEventListener('click', this.onAutoplayClick);
      }

      this.removeEventListener('mouseenter', this.onPointerEnter);
      this.removeEventListener('mouseleave', this.onPointerLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.removeEventListener('wheel', this.onWheel);
      window.removeEventListener('resize', this.scheduleHeightMeasurement);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.wheelTimer) window.clearTimeout(this.wheelTimer);
    }

    initHeightObserver() {
      if (!('ResizeObserver' in window)) return;

      this.heightObserver = new ResizeObserver(this.scheduleHeightMeasurement);
      this.slides.forEach((slide) => {
        const content = slide.querySelector('.sl-sbs-slide__content');
        if (content) this.heightObserver.observe(content);
        slide.querySelectorAll('img, video').forEach((media) => this.heightObserver.observe(media));
      });
    }

    scheduleHeightMeasurement() {
      if (this.heightFrame) window.cancelAnimationFrame(this.heightFrame);
      this.heightFrame = window.requestAnimationFrame(() => {
        this.heightFrame = null;
        this.measureRequiredHeight();
      });
    }

    measureRequiredHeight() {
      if (!this.frame || !this.slides.length) return;

      let requiredHeight = 0;

      this.slides.forEach((slide) => {
        const positioner = slide.querySelector('.sl-sbs-slide__positioner');
        const content = slide.querySelector('.sl-sbs-slide__content');
        if (!positioner || !content) return;

        const positionerStyle = window.getComputedStyle(positioner);
        const paddingTop = Number.parseFloat(positionerStyle.paddingTop) || 0;
        const paddingBottom = Number.parseFloat(positionerStyle.paddingBottom) || 0;
        let contentHeight = content.scrollHeight;

        content.querySelectorAll(':scope > .sl-sbs-image').forEach((image) => {
          contentHeight = Math.max(contentHeight, image.offsetHeight);
        });

        requiredHeight = Math.max(requiredHeight, Math.ceil(contentHeight + paddingTop + paddingBottom));
      });

      if (this.measuredHeight === requiredHeight) return;

      this.measuredHeight = requiredHeight;
      this.style.setProperty('--sl-sbs-measured-height', `${requiredHeight}px`);
      if (this.flkty) this.flkty.resize();
    }

    updateActiveSlide(activeIndex) {
      this.slides.forEach((slide, index) => {
        const active = index === activeIndex;
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        slide.setAttribute('aria-label', `${index + 1} de ${this.slides.length}`);

        slide.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
          if (!active) {
            if (!element.hasAttribute('data-sl-sbs-tabindex')) {
              element.setAttribute('data-sl-sbs-tabindex', element.getAttribute('tabindex') || '');
            }
            element.setAttribute('tabindex', '-1');
          } else if (element.hasAttribute('data-sl-sbs-tabindex')) {
            const previous = element.getAttribute('data-sl-sbs-tabindex');
            if (previous === '') element.removeAttribute('tabindex');
            else element.setAttribute('tabindex', previous);
            element.removeAttribute('data-sl-sbs-tabindex');
          }
        });

        slide.querySelectorAll('video').forEach((video) => {
          if (!active) {
            video.pause();
          } else if (video.hasAttribute('autoplay') && !this.reducedMotion) {
            const playPromise = video.play();
            if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
          }
        });
      });
    }

    onAutoplayClick() {
      if (!this.flkty) return;

      this.userPaused = !this.userPaused;
      this.autoplayControl.setAttribute('aria-pressed', String(this.userPaused));
      this.autoplayControl.setAttribute(
        'aria-label',
        this.userPaused ? 'Retomar apresentação' : 'Pausar apresentação'
      );

      if (this.userPaused) this.flkty.pausePlayer();
      else this.flkty.playPlayer();
    }

    onPointerEnter() {
      if (this.flkty && !this.userPaused) this.flkty.pausePlayer();
    }

    onPointerLeave() {
      if (this.flkty && !this.userPaused && this.dataset.autoplay === 'true' && !this.reducedMotion) {
        this.flkty.playPlayer();
      }
    }

    onFocusIn() {
      if (this.flkty && !this.userPaused) this.flkty.pausePlayer();
    }

    onFocusOut(event) {
      if (this.contains(event.relatedTarget)) return;
      this.onPointerLeave();
    }

    onWheel(event) {
      if (!this.flkty) return;

      const horizontalIntent = Math.abs(event.deltaX) > Math.abs(event.deltaY) && Math.abs(event.deltaX) > 12;
      if (!horizontalIntent) return;

      event.preventDefault();
      if (this.wheelLocked) return;

      this.wheelLocked = true;
      this.flkty.uiChange();
      if (event.deltaX > 0) this.flkty.next();
      else this.flkty.previous();

      this.wheelTimer = window.setTimeout(() => {
        this.wheelLocked = false;
      }, 450);
    }

    onBlockSelect(event) {
      if (!this.flkty || !this.contains(event.target)) return;

      const selectedSlide = event.target.closest('[data-slideshow-by-studiolab-slide]');
      if (!selectedSlide) return;

      const index = this.slides.indexOf(selectedSlide);
      if (index < 0) return;

      this.flkty.select(index, false, true);
      this.flkty.pausePlayer();
    }
  }

  customElements.define('slideshow-by-studiolab', SlideshowByStudiolab);
})();
